import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, inject } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import { addIcons } from 'ionicons';
import { ban, flag } from 'ionicons/icons';
import { JsonapiCore, provideNgxJsonapiStandalone } from 'ngx-jsonapi';
import { filter, take } from 'rxjs';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { authTokenInterceptor } from './app/shared/interceptors/auth-token.interceptor';
import { AnalyticsService } from './app/shared/services/analytics.service';
import { AttributionService } from './app/shared/services/attribution.service';
import { AuthService } from './app/shared/services/auth.service';
import { FeatureFlagsService } from './app/shared/services/feature-flags.service';
import { JsonapiBootstrapService } from './app/shared/services/jsonapi-bootstrap.service';
import { JsonapiRuntimeConfigService } from './app/shared/services/jsonapi-runtime-config.service';
import { LanguageService } from './app/shared/services/language.service';
import { OtaUpdateService } from './app/shared/services/ota-update.service';
import { NotificationStateService } from './app/shared/services/notification-state.service';
import { PushNotificationsService } from './app/shared/services/push-notifications.service';
import { environment } from './environments/environment';

type SentryOptionsWithReplay = Sentry.CapacitorOptions & Pick<
  SentryAngular.BrowserOptions,
  'replaysOnErrorSampleRate' | 'replaysSessionSampleRate'
>;

const sentryOptions: SentryOptionsWithReplay = {
  dsn: 'https://c94f27e7cccfe3127e585204a3226905@o93462.ingest.us.sentry.io/4512016262430720',
  environment: environment.production ? 'production' : 'development',
  integrations: [
    SentryAngular.browserTracingIntegration(),
    SentryAngular.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', environment.apiUrl],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
};

if (environment.sentryEnabled) Sentry.init(sentryOptions, SentryAngular.init);

registerLocaleData(localeEs);
addIcons({ ban, flag });

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler({ showDialog: false }),
    },
    provideIonicAngular(),
    provideHttpClient(withInterceptors([authTokenInterceptor])),
    provideTranslateService({
      fallbackLang: 'es',
      lang: 'es',
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
    }),
    provideNgxJsonapiStandalone(() => inject(JsonapiRuntimeConfigService).resolveConfig()),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        inject(SentryAngular.TraceService);
        const authService = inject(AuthService);
        const featureFlagsService = inject(FeatureFlagsService);
        const analyticsService = inject(AnalyticsService);
        const attributionService = inject(AttributionService);
        const injector = inject(Injector);
        const jsonapiBootstrapService = inject(JsonapiBootstrapService);
        const jsonapiRuntimeConfigService = inject(JsonapiRuntimeConfigService);
        const languageService = inject(LanguageService);
        const otaUpdateService = inject(OtaUpdateService);
        const notificationStateService = inject(NotificationStateService);
        const pushNotificationsService = inject(PushNotificationsService);

        return async () => {
          await languageService.initialize();
          await jsonapiRuntimeConfigService.resolveConfig();
          await attributionService.initialize();
          await analyticsService.initialize();
          injector.get(JsonapiCore);
          await authService.initialize();
          await featureFlagsService.initialize();
          await otaUpdateService.initialize();
          await pushNotificationsService.initialize();
          await notificationStateService.initialize(authService.isAuthenticated());
          await jsonapiBootstrapService.initialize();
        };
      },
      multi: true,
    },
  ],
}).then((applicationRef) => {
  applicationRef.isStable.pipe(
    filter((isStable) => isStable),
    take(1),
  ).subscribe(() => {
    applicationRef.injector.get(OtaUpdateService).markAngularAppReady();
  });
});
